import { sdk } from '../sdk';

export interface Account {
  _id: string;
  accountNumber: string;
  name: string;
  balance: number;
  status: 'active' | 'inactive';
  isCashAccount: boolean;
  autoUpdateFromDonations?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAccountData {
  accountNumber: string;
  name: string;
  balance?: number;
}

export interface UpdateAccountData {
  name?: string;
  balance?: number;
  status?: 'active' | 'inactive';
  autoUpdateFromDonations?: boolean;
}

export interface AccountSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

export const AccountsAPI = {
  // Get all accounts with pagination and filters
  async getAllAccounts(params: AccountSearchParams = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    // Default limit to 50 if not specified
    queryParams.append('limit', (params.limit || 50).toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);

    const response = await sdk.client.get(`/api/v1/accounts?${queryParams.toString()}`);
    return response.data || response;
  },

  // Get active accounts only
  async getActiveAccounts(): Promise<Account[]> {
    const response = await sdk.client.get('/api/v1/accounts/active');
    return response.data || response;
  },

  // Get account by ID
  async getAccountById(id: string): Promise<Account> {
    const response = await sdk.client.get(`/api/v1/accounts/${id}`);
    return response.data || response;
  },

  // Create new account
  async createAccount(data: CreateAccountData): Promise<Account> {
    const response = await sdk.client.post('/api/v1/accounts', data);
    return response.data || response;
  },

  // Update account
  async updateAccount(id: string, data: UpdateAccountData): Promise<Account> {
    const response = await sdk.client.put(`/api/v1/accounts/${id}`, data);
    return response.data || response;
  },

  // Toggle account status
  async toggleAccountStatus(id: string): Promise<Account> {
    const response = await sdk.client.patch(`/api/v1/accounts/${id}/toggle-status`);
    return response.data || response;
  },

  // Delete account
  async deleteAccount(id: string): Promise<{ success: boolean; message: string }> {
    const response = await sdk.client.delete(`/api/v1/accounts/${id}`);
    return response.data || response;
  },

  // Get account statistics
  async getAccountStats(): Promise<any> {
    const response = await sdk.client.get('/api/v1/accounts/stats');
    return response.data || response;
  },

  // Initialize cash account
  async initializeCashAccount(): Promise<Account> {
    const response = await sdk.client.post('/api/v1/accounts/initialize-cash');
    return response.data || response;
  }
};
